import Add16 from '@carbon/icons-react/es/add/16';
import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import { Tabs } from 'antd';
import classNames from 'classnames';
import { isEmpty, isEqual } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AttachmentsAPI } from 'api/attachments';
import { FieldSectionType } from 'api/field-sections/types';
import { IntegrationProvider, JiraConfig } from 'api/integrations';
import {
  MetricsEventAPI,
  MetricsEventNames,
  MetricsEventOrigin,
} from 'api/metrics/events';
import { ProjectId, TaskId } from 'api/models';
import { StatusGroup, Task as TaskApi, TaskAPI, TaskType } from 'api/tasks';
import { Collapse, Panel } from 'components/atoms/collapse';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { DebouncedComponent } from 'components/atoms/utils/debounced-component';
import { Option, SquadPayload } from 'components/molecules/squad-picker/types';
import { optionsToSquadPayload } from 'components/molecules/squad-picker/utils';
import {
  AttachmentsTabViews,
  TaskAttachments,
} from 'components/molecules/tasks/details/attachments';
import { TaskCsat } from 'components/molecules/tasks/details/csat';
import { DependentTasks } from 'components/molecules/tasks/details/dependent-tasks';
import { TaskDescription } from 'components/molecules/tasks/details/description';
import {
  JiraIssueDetails,
  JiraIssuesTitle,
} from 'components/molecules/tasks/details/jira-issue-details';
import { JiraLinkIssue } from 'components/molecules/tasks/details/jira-link-issue/jira-link-issue';
import { JiraLinkIssueTitle } from 'components/molecules/tasks/details/jira-link-issue/title';
import { PrivateNote } from 'components/molecules/tasks/details/private-note';
import { SubTasks } from 'components/molecules/tasks/details/sub-tasks';
import { SubTasksList } from 'components/molecules/tasks/details/sub-tasks/sub-tasks';
import { TaskFields } from 'components/molecules/tasks/details/task-fields';
import { TimeEntrySetting } from 'components/molecules/tasks/details/time-entry-settings';
import { SectionPanelKeyHash } from 'components/molecules/tasks/details/types';
import { computeSectionFieldMapping } from 'components/organisms/field-sections/utils';
import {
  canShowInTaskFieldsSettings,
  taskField,
} from 'components/organisms/module-settings/fields/utils';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { RbacPanel } from 'components/organisms/rbac/components/rbac-collapse';
import { RbacPrivateNoteEditor } from 'components/organisms/rbac/components/rbac-private-note';
import { RbacTaskDescriptionEditor } from 'components/organisms/rbac/components/rbac-rte';
import { RbacTimeEntrySettingList } from 'components/organisms/rbac/components/rbac-time-entry-setting';
import { ImageHandler } from 'components/organisms/task-pane/task-detail/description';
import {
  Main,
  StyledTabs,
  TabPaneScrollWrapper,
} from 'components/organisms/task-pane/task-detail/styled';
import { FetchStatus } from 'data/core';
import { useSelectedStatusOption } from 'data/fields/hooks/account-fields';
import { useProjectDetail } from 'data/projects';
import { Task } from 'data/tasks';
import {
  deleteTaskSagaAction,
  dependencyCreatedAction,
  dependencyRemovedAction,
  removeAsSubtaskSagaAction,
  UpdateTaskActionPayloadOptions,
  updateTaskPrivacySagaAction,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { selectTaskFetchStatus } from 'data/tasks/selectors';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useCheckProjectBillability } from 'hooks/use-check-project-billablility';
import {
  ClickEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { TaskDrawerTab, useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useScrollTop } from 'hooks/use-scroll-top';
import { useUser } from 'hooks/use-user';
import { useAllFieldsQuery } from 'queries';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import {
  getProjectDependencyMapKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { useFieldSectionsQuery } from 'queries/field-sections';
import { booleanAttr } from 'utils/boolean-attr';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import { DependentTasksList } from './dependencies';
import { TaskFormRenderer } from './forms';
import { Header } from './header';
import { HeaderTaskName } from './header/header-taskname';
import { AtRisk, Highlight } from './highlight';
import { PrivatePlacard } from './private-placard';
import styles from './task-detail.module.css';
import { TaskFields as TaskFieldsSection } from './task-fields';
import { addValuesToFields } from './task-fields/task-custom-fields';
import { usePopulateUsersMetaInFields } from './task-fields/task-custom-fields/use-task-custom-fields';

const StyledTabsWithSticky = styled(StyledTabs)`
  .ant-tabs-nav {
    position: sticky;
    top: 92px;
    z-index: var(--zindex-layer-08);
    background-color: var(--white-100);
  }
`;

const StyledContainer = styled.div`
  height: calc(100vh - 104px);
`;

const AddButton = styled(RbacButton)`
  padding-left: var(--spacing-03);
`;

export const TaskTabKeys = {
  Form: 'form',
  BasicDetails: 'basic_details',
  AdditionalDetails: 'additional_details',
};

export const TaskDetail: FC<{
  handleJiraIssueCreate: () => void;
  onCreate?: (taskId) => void;
  onDelete?(): void;
  data: Task;
  jiraConfig?: JiraConfig;
}> = (props) => {
  const { hasAccessToResource } = useWithAccess();
  const { onCreate, onDelete, data, handleJiraIssueCreate, jiraConfig } = props;
  const { taskId, project } = data;
  const projectId = project;
  const [showPrivateNoteEditor, setPrivateNoteEditorVisibility] =
    useState<boolean>(!!data.privateTaskDescription?.length);
  const [showAttachmentPanel, setAttachmentPanelVisibility] = useState<boolean>(
    !!data.attachments?.length,
  );

  const [view, setView] = useState(AttachmentsTabViews.GridView);
  const [showJiraIssueLink, setShowJiraIssueLink] = useState(false);
  const alignPhaseDates = useGetAlignPhaseDatesSetting();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const updateTask = (payload, options?: UpdateTaskActionPayloadOptions) => {
    dispatch(
      updateTaskSagaAction({ projectId, taskId, data: payload, options }),
    );
  };

  const navigateToTask = useNavigateToTask();
  const openTaskPane = (record: TaskApi) => {
    navigateToTask(TaskId(projectId, record.taskId));
  };

  const userInfo = useUser();

  const selectKeys = useCallback((): string[] => {
    const collapsibleSectionKeys: string[] = [
      SectionPanelKeyHash.Description,
      SectionPanelKeyHash.PrivateNote,
      SectionPanelKeyHash.JiraIssues,
      SectionPanelKeyHash.TimeEntrySetting,
      SectionPanelKeyHash.Csat,
      SectionPanelKeyHash.JiraLinkIssue,
    ];

    if (
      data.attachments?.length &&
      !collapsibleSectionKeys.includes(SectionPanelKeyHash.Attachments)
    ) {
      collapsibleSectionKeys.push(SectionPanelKeyHash.Attachments);
    }
    if (
      data.subTasks?.length &&
      !collapsibleSectionKeys.includes(SectionPanelKeyHash.SubTasks)
    ) {
      collapsibleSectionKeys.push(SectionPanelKeyHash.SubTasks);
    }
    if (
      data.dependentTasks?.length &&
      !collapsibleSectionKeys.includes(SectionPanelKeyHash.DependentTasks)
    ) {
      collapsibleSectionKeys.push(SectionPanelKeyHash.DependentTasks);
    }
    if (
      data.fields?.length &&
      !collapsibleSectionKeys.includes(SectionPanelKeyHash.TaskFields)
    ) {
      collapsibleSectionKeys.push(SectionPanelKeyHash.TaskFields);
    }

    return collapsibleSectionKeys;
  }, [
    data.attachments?.length,
    data.dependentTasks?.length,
    data.fields?.length,
    data.subTasks?.length,
  ]);

  const [collapseActiveKeys, setCollapseActiveKeys] = useState<
    string[] | string
  >(selectKeys());

  const taskFetchStatus = useSelector(selectTaskFetchStatus(taskId));

  useEffect(() => {
    if (taskFetchStatus === FetchStatus.Loaded) {
      setCollapseActiveKeys(selectKeys());
    }
  }, [selectKeys, taskFetchStatus]);

  const handleConfirmDelete = () => {
    dispatch(deleteTaskSagaAction({ projectId, taskId }));
    if (onDelete) {
      onDelete();
    }
  };

  useEffect(() => {
    const payload = {
      originId: data.taskId,
      originType: MetricsEventOrigin.Task,
      eventName: MetricsEventNames.TaskPaneViewed,
    };

    MetricsEventAPI.postMetricsEvent(payload);
  }, [data.taskId]);
  const handleRequestApprovalClick = () => {
    navigateToTask(TaskId(projectId, data.taskId), {
      tab: TaskDrawerTab.Approvals,
      showNewApproval: 'true',
    });
  };

  const handleTaskNameChange = (taskName: string) => {
    /*  NOTE:: Product requirement on task create is not yet clear.
        So temporarily creating task with task name.
        TODO:: Refactor once product requirement is clear. */
    if (isEqual(data?.taskName?.trim(), taskName.trim())) {
      return;
    }
    if (!data?.taskId) {
      TaskAPI.createTask(projectId, { taskName }).then((res) => {
        // setData(res);
        if (onCreate) {
          onCreate(res.taskId);
        }
      });
    } else {
      updateTask({ taskName });
    }
  };

  const handleDueDateChange = ([selectedStartDate, selectedDueDate]) => {
    if (selectedDueDate !== undefined || selectedStartDate !== undefined) {
      const payload: { [key: string]: number | null } = {};

      if (selectedStartDate !== undefined) {
        payload.startDate = selectedStartDate;
      }
      if (selectedDueDate !== undefined) {
        payload.dueDate = selectedDueDate;
      }
      updateTask(payload);
    } else {
      updateTask({
        dueDate: null,
        startDate: null,
      });
    }
  };
  const { TabPane } = Tabs;
  const [ref, isScrolled] = useScrollTop();

  const headerClassName = classNames({ [styles.header_shadow]: isScrolled });

  const handleMarkAtRisk = () => {
    updateTask({ atRisk: !data.atRisk });
  };

  const togglePrivate = () => {
    dispatch(
      updateTaskPrivacySagaAction({
        taskId,
        private: !data.private,
        projectId,
      }),
    );
  };

  const handleConvertToMilestone = () => {
    updateTask({
      type:
        data.type === TaskType.Milestone ? TaskType.Task : TaskType.Milestone,
    });
  };

  const handleAssigneeChange = (
    assignee: Option[],
    resolveCallback?: (value) => void,
  ) => {
    return updateTask(
      { assignee: optionsToSquadPayload(assignee) },
      { resolveCallback },
    );
  };

  const handleFollowersChange = (followers: SquadPayload) => {
    return updateTask({ followers });
  };

  const updateTaskDescription = (taskDescription: string) => {
    if (taskDescription !== data.taskDescription) {
      updateTask({
        taskDescription,
      });
    }
  };
  const updatePrivateTaskDescription = (privateTaskDescription: string) => {
    if (privateTaskDescription !== data?.privateTaskDescription) {
      updateTask({
        privateTaskDescription,
      });
    }
  };
  const fetchDependentTasks = () => TaskAPI.getTaskList(projectId);
  const createDependency = (task: TaskApi) => {
    TaskAPI.createDependentTask(projectId, taskId, {
      dependentTaskIds: [task.taskId],
    }).then(async () => {
      await queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
      await queryClient.refetchQueries({
        predicate: (query) => {
          return (
            query.queryHash.includes('task-detail') &&
            query?.queryHash.includes(taskId.toString())
          );
        },
      });

      const queryTasks = queryClient.getQueryData<Task[]>(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      if (queryTasks) {
        let taskData = queryTasks.find((t) => t.taskId === taskId);
        const dependentTaskData = queryTasks?.find(
          (t) => t.taskId === task.taskId,
        );

        if (taskData && dependentTaskData) {
          taskData = {
            ...taskData,
            dependentTasks: [
              ...(taskData.dependentTasks ?? []),
              dependentTaskData,
            ],
          };

          const updatedTasks = queryTasks.map((t) => {
            if (t.taskId === taskId) {
              return taskData;
            }

            return t;
          });

          queryClient.setQueryData(
            getProjectTasksQueryKey(ProjectId(projectId)),
            updatedTasks,
          );
        }
      }

      dispatch(
        dependencyCreatedAction({
          taskId,
          projectId,
          dependentTaskId: task.taskId,
          canAssignDates: true,
          isSync: false,
          handleNullDates: true,
          alignPhaseDates,
        }),
      );
    });
  };

  const removeDependency = (task: TaskApi) => {
    TaskAPI.removeDependentTask(projectId, taskId, {
      dependentTaskIds: [task.taskId],
    }).then(async () => {
      await queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
      await invalidateTaskQuery(taskId);

      const queryTasks = queryClient.getQueryData<Task[]>(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      if (queryTasks) {
        let taskData = queryTasks.find((t) => t.taskId === taskId);
        const dependentTaskData = queryTasks?.find(
          (t) => t.taskId === task.taskId,
        );

        if (taskData && dependentTaskData) {
          taskData = {
            ...taskData,
            dependentTasks: taskData.dependentTasks?.filter(
              (t) => t.taskId !== task.taskId,
            ),
          };

          const updatedTasks = queryTasks.map((t) => {
            if (t.taskId === taskId) {
              return taskData;
            }

            return t;
          });

          queryClient.setQueryData(
            getProjectTasksQueryKey(ProjectId(projectId)),
            updatedTasks,
          );
        }
      }

      dispatch(
        dependencyRemovedAction({
          taskId,
          dependentTaskId: task.taskId,
          canAssignDates: false,
        }),
      );
    });
  };

  const onUploadAttachment = (file: File) =>
    AttachmentsAPI.attachTaskFiles(file, { taskId, projectId });

  const updateCsatStatus = (isCsatEnabled: boolean) => {
    updateTask({
      isCsatEnabled,
    });
  };

  const handleRemoveAsSubtask = () => {
    dispatch(
      removeAsSubtaskSagaAction({
        projectId,
        taskId,
        queryClient,
        parentTaskId: data.parentTask,
        phaseId: data.projectPhase?.projectPhaseId,
      }),
    );
    if (onDelete) {
      onDelete();
    }
  };

  const selectedStatusOption = useSelectedStatusOption(data.fields);

  const jiraResourceMapping = useMemo(
    () =>
      data.linkedResources?.find(
        ({ externalIntegrationType }) =>
          externalIntegrationType === IntegrationProvider.Jira,
      ),
    [data],
  );

  const jiraIssueId = jiraResourceMapping?.externalResourceId;

  const jiraResourceMappingId = jiraResourceMapping?.resourceMappingId;

  const openPrivatePanel = () => {
    setPrivateNoteEditorVisibility(true);
  };

  const [timesheetsEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const canShowCsat = data.type === TaskType.Milestone && !data.private;
  const { checkIsProjectNonBillable: checkProjectBillability } =
    useCheckProjectBillability();
  const isProjectNonBillable = useMemo(
    () => checkProjectBillability(projectId),
    [checkProjectBillability, projectId],
  );
  const { data: fieldsList } = useAllFieldsQuery();

  const taskFieldsList = useMemo(() => {
    return fieldsList
      ? fieldsList
          .filter(taskField)
          .filter(canShowInTaskFieldsSettings)
          .filter((field) => field.isEnabled)
      : [];
  }, [fieldsList]);

  const taskFields = usePopulateUsersMetaInFields(
    taskFieldsList ?? [],
    ProjectId(projectId),
  );

  const { data: fieldSections } = useFieldSectionsQuery(FieldSectionType.Task);

  const fieldsWithValues = useMemo(() => {
    return addValuesToFields(taskFields, data?.fields ?? []);
  }, [taskFields, data?.fields]);

  const fieldSectionMapping = useMemo(() => {
    const customSections = fieldSections?.filter((section) => !section.fixed);
    const sectionFieldMapping = computeSectionFieldMapping(
      fieldsWithValues,
      customSections,
    );

    return sectionFieldMapping;
  }, [fieldsWithValues, fieldSections]);

  const handleJiraIssueLink = useCallback(() => setShowJiraIssueLink(true), []);

  const onAddAttachment = useCallback(() => {
    setAttachmentPanelVisibility(true);
    setCollapseActiveKeys((keys) => [...keys, SectionPanelKeyHash.Attachments]);
  }, []);

  const canShowForms = data.formInstanceId;

  useLoadLoggerForDataDog([
    ClickEventTags.ProjectPlanTaskLoad,
    ClickEventTags.HomeTaskLoad,
    ClickEventTags.AllTasksTaskLoad,
  ]);

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );

  return (
    <>
      <Helmet>
        <title>{data.taskName}</title>
      </Helmet>
      <HeaderTaskName
        className={headerClassName}
        data={data}
        projectId={projectId}
        handleNameChange={handleTaskNameChange}
        projectResource={projectResource}
      />
      <StyledContainer>
        <Header
          className={headerClassName}
          data={data}
          projectId={projectId}
          showJiraCreate={!isEmpty(jiraConfig)}
          handleAssigneeChange={handleAssigneeChange}
          handleDueDateChange={handleDueDateChange}
          handleFollowersChange={handleFollowersChange}
          handleConfirmDelete={handleConfirmDelete}
          handleMarkAtRisk={handleMarkAtRisk}
          togglePrivate={togglePrivate}
          handleConvertToMilestone={handleConvertToMilestone}
          handleApproval={handleRequestApprovalClick}
          handleJiraIssueCreate={handleJiraIssueCreate}
          handleJiraIssueLink={handleJiraIssueLink}
          canCreateJiraIssue={!isEmpty(jiraConfig) && !jiraIssueId}
          jiraAuth={jiraConfig?.integrationStatus}
          isProjectNonBillable={isProjectNonBillable}
          handleRemoveAsSubtask={handleRemoveAsSubtask}
        />
        <Main $private={booleanAttr(data.private)} ref={ref}>
          {data?.private && (
            <PrivatePlacard accountName={userInfo.company.companyName} />
          )}
          {data.atRisk && (
            <Highlight>
              <AtRisk />
            </Highlight>
          )}
          <StyledTabsWithSticky
            defaultActiveKey={
              canShowForms ? TaskTabKeys.Form : TaskTabKeys.BasicDetails
            }
            tabBarGutter={1}
          >
            {data.formInstanceId && (
              <TabPane
                tab={
                  <span data-cy="task_details.tabs.form">
                    <FormattedMessage id="form" />
                  </span>
                }
                key={TaskTabKeys.Form}
                id="task_details.tabs.form"
              >
                <TaskFormRenderer
                  projectId={data.project}
                  taskId={data.taskId}
                  formResponseCount={data.formDetails?.responseCount}
                  instanceId={data.formInstanceId}
                  responseId={data.formDetails?.formAnswerId}
                />
              </TabPane>
            )}
            <TabPane
              tab={
                <span data-cy="task_details.tabs.basic_details">
                  <FormattedMessage id="basic_details" />
                </span>
              }
              key={TaskTabKeys.BasicDetails}
            >
              <TabPaneScrollWrapper>
                <Collapse
                  className={styles.collapse}
                  activeKey={collapseActiveKeys}
                  onChange={(_keys) => {
                    setCollapseActiveKeys(_keys);
                  }}
                  expandIconPosition="right"
                  expandIcon={({ isActive }) => {
                    return isActive ? <CaretDown16 /> : <CaretUp16 />;
                  }}
                >
                  <Panel
                    key={SectionPanelKeyHash.Description}
                    header={<TaskDescription.Title />}
                  >
                    <DebouncedComponent delay={0}>
                      <ImageHandler canOpenImage>
                        <RbacTaskDescriptionEditor
                          taskDescription={data.taskDescription}
                          updateTask={updateTaskDescription}
                          projectId={data.project}
                          rule="TASK_DESCRIPTION:WRITE"
                          resourceData={projectResource}
                          propsIfNoAccess={{ disabled: true }}
                        />
                      </ImageHandler>
                    </DebouncedComponent>
                    {!showPrivateNoteEditor ? (
                      <AddButton
                        data-cy="task_drawer.add_private_note"
                        type="link"
                        size="small"
                        icon={<Add16 />}
                        onClick={openPrivatePanel}
                        rule="TASK_DESCRIPTION:WRITE"
                        actionScope={['PRIVATE']}
                        resourceData={projectResource}
                      >
                        <FormattedMessage id="task.private_note.add" />
                      </AddButton>
                    ) : null}
                    {!showAttachmentPanel && !data.attachments?.length ? (
                      <AddButton
                        type="link"
                        size="small"
                        icon={<Add16 />}
                        data-cy="task.detail.add-attachment-button"
                        onClick={onAddAttachment}
                        rule="TASK_ATTACHMENT:WRITE"
                        resourceData={projectResource}
                      >
                        <FormattedMessage id="attachment.add" />
                      </AddButton>
                    ) : null}
                  </Panel>
                  {showPrivateNoteEditor ? (
                    <Panel
                      key={SectionPanelKeyHash.PrivateNote}
                      header={<PrivateNote.Title />}
                    >
                      <DebouncedComponent delay={0}>
                        <RbacPrivateNoteEditor
                          note={data.privateTaskDescription}
                          onUpdate={updatePrivateTaskDescription}
                          projectId={data.project}
                          rule="TASK_DESCRIPTION:WRITE"
                          actionScope={['PRIVATE']}
                          resourceData={projectResource}
                          propsIfNoAccess={{ disabled: true }}
                        />
                      </DebouncedComponent>
                    </Panel>
                  ) : null}

                  {jiraConfig?.settingsMeta.fixedProjectId &&
                    jiraIssueId &&
                    jiraResourceMappingId && (
                      <Panel
                        id={SectionPanelKeyHash.JiraIssues}
                        key={SectionPanelKeyHash.JiraIssues}
                        className={styles.section__panel}
                        header={<JiraIssuesTitle />}
                      >
                        <JiraIssueDetails
                          projectId={projectId}
                          jiraConfig={jiraConfig}
                          jiraIssueId={jiraIssueId}
                          resourceMappingId={jiraResourceMappingId}
                          context={{ type: 'TASK', id: taskId }}
                        />
                      </Panel>
                    )}
                  {showJiraIssueLink && jiraConfig && !jiraResourceMappingId && (
                    <Panel
                      id={SectionPanelKeyHash.JiraLinkIssue}
                      key={SectionPanelKeyHash.JiraLinkIssue}
                      className={styles.section__panel}
                      header={<JiraLinkIssueTitle />}
                    >
                      <JiraLinkIssue
                        onClose={() => setShowJiraIssueLink(false)}
                        jiraIntegrationId={jiraConfig.integrationId}
                        taskId={data.taskId}
                        projectId={projectId}
                      />
                    </Panel>
                  )}
                  {(showAttachmentPanel || data.attachments?.length) && (
                    <Panel
                      id={SectionPanelKeyHash.Attachments}
                      key={SectionPanelKeyHash.Attachments}
                      className={styles.section__panel}
                      header={
                        <TaskAttachments.Title
                          noOfItems={
                            data.attachments ? data.attachments?.length : 0
                          }
                        />
                      }
                      extra={
                        data.attachments &&
                        data.attachments?.length > 0 && (
                          <TaskAttachments.Tabs
                            taskId={taskId}
                            projectId={projectId}
                            onChooseTab={setView}
                          />
                        )
                      }
                    >
                      <TaskAttachments.List
                        view={view}
                        data={data.attachments}
                        onUploadRequest={onUploadAttachment}
                        onMount={(uploadButtonRef) => {
                          if (!data.attachments?.length) {
                            uploadButtonRef.current.click();
                          }
                        }}
                        projectResource={projectResource}
                      />
                    </Panel>
                  )}
                  <RbacPanel
                    id={SectionPanelKeyHash.SubTasks}
                    key={SectionPanelKeyHash.SubTasks}
                    className={`${styles.section__panel}`}
                    header={
                      <SubTasks.Title
                        noOfItems={data.subTasks ? data.subTasks?.length : 0}
                      />
                    }
                    rule="SUBTASK:READ"
                    resourceData={projectResource}
                  >
                    <SubTasksList
                      data={data.subTaskOrder ?? []}
                      projectId={projectId}
                      taskId={data.taskId}
                      isExpanded={false}
                    />
                  </RbacPanel>
                  <RbacPanel
                    id={SectionPanelKeyHash.DependentTasks}
                    key={SectionPanelKeyHash.DependentTasks}
                    className={styles.section__panel}
                    header={
                      <DependentTasks.Title
                        noOfItems={
                          data.dependentTasks ? data.dependentTasks?.length : 0
                        }
                      />
                    }
                    rule="TASK_DEPENDENCY:READ"
                    resourceData={projectResource}
                  >
                    <DependentTasksList
                      data={data.dependentTasks as unknown as TaskApi[]}
                      taskId={data.taskId}
                      onFetchTasks={fetchDependentTasks}
                      onCreateDependency={createDependency}
                      onRemoveDependency={removeDependency}
                      onTaskClick={openTaskPane}
                      projectId={projectId}
                    />
                  </RbacPanel>
                </Collapse>
              </TabPaneScrollWrapper>
            </TabPane>
            <TabPane
              tab={
                <span data-cy="task_details.tabs.additional_info">
                  <FormattedMessage id="additional_details" />
                </span>
              }
              key={TaskTabKeys.AdditionalDetails}
              id="task_details.tabs.additional_details"
            >
              <TabPaneScrollWrapper data-cy="task_details.tabs.additional_info-content">
                <Collapse
                  className={styles.collapse}
                  activeKey={collapseActiveKeys}
                  onChange={(_keys) => {
                    setCollapseActiveKeys(_keys);
                  }}
                  expandIconPosition="right"
                  expandIcon={({ isActive }) => {
                    return isActive ? <CaretDown16 /> : <CaretUp16 />;
                  }}
                >
                  <Panel
                    id={SectionPanelKeyHash.TaskFields}
                    key={SectionPanelKeyHash.TaskFields}
                    className={styles.section__panel}
                    header={<TaskFields.Title />}
                  >
                    <TaskFieldsSection
                      data={data}
                      fieldSectionMapping={fieldSectionMapping}
                      fieldsWithValues={fieldsWithValues}
                    />
                  </Panel>
                  {/* TODO: check if this need to be hidden or disabled */}
                  {timesheetsEnabled ? (
                    <RbacPanel
                      key={SectionPanelKeyHash.TimeEntrySetting}
                      className={styles.section__panel}
                      header={<TimeEntrySetting.Title />}
                      rule="TIMESHEET:READ"
                    >
                      <RbacTimeEntrySettingList
                        task={
                          {
                            ...data,
                            defaultTimeEntryBillable: isProjectNonBillable
                              ? false
                              : data.defaultTimeEntryBillable,
                            defaultTimeEntryCategory:
                              data.defaultTimeEntryCategory,
                          } as any
                        }
                        onUpdateTask={updateTask}
                        rule="TIMESHEET:WRITE"
                        propsIfNoAccess={{
                          disabled: true,
                        }}
                      />
                    </RbacPanel>
                  ) : null}
                  {canShowCsat && (
                    <RbacPanel
                      id={SectionPanelKeyHash.Csat}
                      key={SectionPanelKeyHash.Csat}
                      className={styles.section__panel}
                      header={<TaskCsat.Title />}
                      extra={
                        <HideForCustomer>
                          <TaskCsat.Status
                            disabled={
                              selectedStatusOption?.group ===
                                StatusGroup.Completed ||
                              !hasAccessToResource({
                                rule: 'TASK_TYPE:WRITE',
                                // This is the permission needed for converting task to milestone
                              })
                            }
                            checked={data.isCsatEnabled}
                            onChange={updateCsatStatus}
                          />
                        </HideForCustomer>
                      }
                      // rule="TASK_CSAT:WRITE"
                      // resourceData={projectResource}
                      propsIfNoAccess={{
                        disabled: true,
                      }}
                    >
                      <TaskCsat.Detail
                        name={data.taskName}
                        isWaiting={
                          selectedStatusOption?.group ===
                            StatusGroup.Completed &&
                          Boolean(data.isCsatEnabled) &&
                          !data.csatRatings?.length
                        }
                        ratings={data.csatRatings || []}
                      />
                    </RbacPanel>
                  )}
                </Collapse>
              </TabPaneScrollWrapper>
            </TabPane>
          </StyledTabsWithSticky>
        </Main>
      </StyledContainer>
    </>
  );
};
